import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { SlidingPanel, Tabs } from 'core/components';
import { AgGridFilterOption, SortDirection } from 'core/enums';
import {
  CorporatePartCostList,
  CorporatePartSetUpList,
  CorporatePartUom,
} from 'corporate/components';
import { useGetCorporatePart } from 'corporate/hooks';
import { IComponentTemplate } from 'elements/types';
import { History, Inventory, PartCards, Subtitle } from 'parts/components/PartDetails';
import { PartDetailsFocusProvider } from 'parts/components/PartDetails/hooks';
import { PartNumberHelper } from 'parts/helpers';
import { useGetPart } from 'parts/hooks';
import { IPart } from 'parts/types';
import { partToManufacturer, partToPartDetails } from 'parts/utils';

import { PartDetailsDrawerTab } from './enums';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  part: IComponentTemplate | null;
}

export const PartDetailsDrawer = ({ isOpen, onClose, part }: IProps) => {
  const { t } = useTranslation();

  const mappedPart = useMemo(
    () =>
      part
        ? ({
            id: part.partId,
            number: part.part,
            manufacturerId: part.manufacturerId,
            manufacturerName: part.manufacturerName,
            manufacturerUrlName: part.manufacturerUrlName,
            familyId: part.partFamilyId,
            verified: part.verified,
            description: part.partDescription,
            descriptionLong: part.partDescriptionLong,
          } as IPart)
        : null,
    [part]
  );

  const { record, isLoading } = useGetCorporatePart(
    {
      part: {
        number: mappedPart?.number,
        manufacturerId: mappedPart?.manufacturerId,
        verified: mappedPart?.verified,
      },
    },
    isOpen && !!part?.id
  );

  const { part: partDetails, manufacturer: manufacturerDetails } = useGetPart(
    { number: mappedPart?.number ?? '', manufacturer: mappedPart?.manufacturerUrlName ?? '' },
    isOpen && !!mappedPart?.number && !!mappedPart?.manufacturerUrlName
  );

  const corporatePart = useMemo(() => record || {}, [record]);

  // BE returning null is treated the same as "still loading" — the IPart row is the source
  // of truth for unverified/missing records.
  const subtitlePart = useMemo(
    () => partDetails ?? (mappedPart ? partToPartDetails(mappedPart) : null),
    [partDetails, mappedPart]
  );

  const subtitleManufacturer = useMemo(
    () => manufacturerDetails ?? (mappedPart ? partToManufacturer(mappedPart) : null),
    [manufacturerDetails, mappedPart]
  );

  const isVerified = !!part?.verified;

  const tabs = [
    {
      value: PartDetailsDrawerTab.HISTORY,
      label: t('labelHistory'),
      content: (
        <History
          initialState={{
            filter: {
              filterModel: {
                updatedAt: {
                  filterType: 'date',
                  type: AgGridFilterOption.RELATIVE_LAST_90_DAYS,
                },
              },
            },
            sort: {
              sortModel: [{ colId: 'updatedAt', sort: SortDirection.DESC }],
            },
          }}
          requestPayload={{
            filters: {
              partNumber: part?.part,
              manufacturerId: part?.manufacturerId,
            },
          }}
        />
      ),
    },
    {
      value: PartDetailsDrawerTab.COSTS,
      label: t('labelCosts'),
      content: (
        <CorporatePartCostList
          enabled={isOpen}
          corporatePart={corporatePart}
          partNumber={part?.part}
          manufacturerId={part?.manufacturerId}
          isLoading={isLoading}
        />
      ),
      hide: !isVerified,
    },
    {
      value: PartDetailsDrawerTab.UOM,
      label: t('labelUoms'),
      content: (
        <CorporatePartUom
          enabled={isOpen}
          corporatePart={corporatePart}
          partNumber={part?.part}
          manufacturerId={part?.manufacturerId}
          isLoading={isLoading}
        />
      ),
      hide: !isVerified,
    },
    {
      value: PartDetailsDrawerTab.SET_UP,
      label: t('labelPartDetails'),
      content: (
        <CorporatePartSetUpList
          corporatePart={corporatePart}
          partNumber={part?.part}
          manufacturerId={part?.manufacturerId}
          isLoading={isLoading}
        />
      ),
      hide: !isVerified,
    },
    {
      value: PartDetailsDrawerTab.INVENTORY,
      label: t('labelInventory'),
      content: <Inventory part={mappedPart} enabled={isOpen} />,
      hide: !isVerified,
    },
  ];

  return (
    <PartDetailsFocusProvider>
      <SlidingPanel
        isOpen={isOpen && !!part?.id}
        onClosePanel={onClose}
        title={
          <Trans
            i18nKey="titlePartDetails"
            values={{
              partNumber: PartNumberHelper.toLabel(part?.part),
            }}
          />
        }
        subtitle={<Subtitle part={subtitlePart} manufacturer={subtitleManufacturer} />}
        extra={
          isOpen && <PartCards corporatePart={corporatePart} isCorporatePartLoading={isLoading} />
        }
        autoWidth
        interactive
      >
        {isOpen && <Tabs tabs={tabs} ariaLabel="partDetailsDrawer" />}
      </SlidingPanel>
    </PartDetailsFocusProvider>
  );
};
